/*
* Report page
*/

import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';

import * as ReportComponents from '../report-components';
import { Formatters } from '../../../common/formatters';
import { getLastNumber } from 'src/utils/numberUtils';

const PRELIMINARY_RESERVE_STUDY_INTRO_DESCRIPTION =
    'Information to complete this Preliminary Reserve Study was gathered from plans. In addition, '
    + 'we may also have obtained information by contacting any vendors and/or contractors that have '
    + 'worked on the property recently, as well as communicating with the property representative '
    + '(BOD Member, Community Manager, developer, etc.). To the best of our knowledge, the conclusions '
    + 'and recommendations of this report are considered reliable and accurate insofar as the information '
    + 'obtained from these sources. Note: We recommend updating this study with a Site Visit once '
    + 'the common elements are completed.';

export const Render = ({ registerFunc, refFunc, scrollToFunc, allowOverrides = true, sectionPages, pageTitle = '', className = '', pageCount, reportData, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.executiveSummary;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);

    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData, reportPeriodStartDate } = reportData;
    // console.log('assoc', { formData, association, reportData });

    // COMMON
    const yearSummary = _.find(yearlySummaryData, ['year', formData.beginYear]);
    const showMinimum = _.get(currentOrderChart, 'showMinimum', true);
    const perFirstYear = Number(_.get(reportData, 'currentOrderChart.perFirstYear', 12));

    // 20191201 Fix for 20258 Exec Summary dates not aligned with cover page dates
    const reportPeriodStart = ReportComponents.shortDate(moment(reportPeriodStartDate).toDate());

    // ABBREVIATED FIRST YEAR?
    let abbreviated = (perFirstYear < 12) ? (
        <ReportComponents.ExecutiveParagraphSection title="" body="">
            <div className="pdf-executive-summary-abbreviated">* This Reserve Study was created for an abbreviated first year reporting period.</div>
        </ReportComponents.ExecutiveParagraphSection>
    ) : (<div />);

    // INFO BLOCK    
    const startingBalance = _.toNumber(_.get(currentOrder, 'startingBalance', 0));
    const begBalance = _.toNumber(_.get(currentOrderChart, 'begBalance', startingBalance));
    const idealBalance = _.toNumber(_.get(yearSummary, 'ffb', begBalance));
    const percentageFunded = percentFunded > 0 ? percentFunded / 100 : 0;

    const minimumMrc = showMinimum ? (
        <ReportComponents.ExecutiveInfo title={`Minimum Reserve Contribution (per month)`} value={<ReportComponents.Currency value={currentOrderChart.minimumMRCVal} decimals={0} />} />
    ) : (<div />);

    // PROPERTY DETAILS
    let propertyDetails = Formatters.getStr(association, 'details.executiveSummaryDetails', '');
    if (!propertyDetails) {
        propertyDetails = `${Formatters.getStr(association, 'name')} is a `;

        if (Formatters.getInt(association, 'details.numberUnits')) {
            propertyDetails += `${Formatters.getInt(association, 'details.numberUnits')}-unit `;
        }

        propertyDetails += `${Formatters.getStr(association, 'associationType.name')} community. `;

        if (Formatters.getStr(association, 'details.constructDate')) {
            propertyDetails += `Construction on the community was completed in ${Formatters.getStr(association, 'details.constructDate')}.`;
        }
    }

    const currentlyProgrammedProjects = (
        <ReportComponents.ExecutiveSummaryCurrentyProgrammedProjects
            inventory={inventory}
            yearlySummaryData={yearlySummaryData}
            yearSummary={yearSummary}
            allowOverrides={allowOverrides}
            currentOrder={currentOrder}
            currentOrderChart={currentOrderChart}
            formData={formData}
            sectionPages={sectionPages}
        />
    );

    const significantProjects = (
        <ReportComponents.ExecutiveSummarySignificantProjects
            inventory={inventory}
            allowOverrides={allowOverrides}
            currentOrder={currentOrder}
            sectionPages={sectionPages}
        />
    );

    const reserveFunding = (
        <ReportComponents.ExecutiveSummaryReserveFunding
            begBalance={begBalance}
            idealBalance={idealBalance}
            percentFunded={percentFunded}
            percentageFunded={percentageFunded}
            showMinimum={showMinimum}
            numberUnits={association.details.numberUnits}
            allowOverrides={allowOverrides}
            currentOrder={currentOrder}
            currentOrderChart={currentOrderChart}
            sectionPages={sectionPages}
        />
    );

    const buildAdditionalAssessments = (assessments: any) => {
        let results = _.stubArray();
        _.forEach(assessments, (entry) => {
            results.push(
                <ReportComponents.ExecutiveInfo
                    title={`Recommended Special Assessment (FY ${entry.year})`}
                    value={<ReportComponents.Currency value={entry.amount} decimals={0} />}
                />
            );
        });

        return results;
    };

    const specialAssessmentsChartRaw = _.get(currentOrderChart, 'specialAssessments', []);
    const specialAssessmentsChart = (_.size(specialAssessmentsChartRaw) > 0) ? _.get(specialAssessmentsChartRaw, [0]) : [];
    const showAdditionalAssessments = (_.size(specialAssessmentsChart) > 0);
    const additionalAssessments = (showAdditionalAssessments) ? buildAdditionalAssessments(specialAssessmentsChart) : (<div />);

    // CUSTOM EXECUTIVE SUMMARY SECTION
    const customExecSummary = (_.get(currentOrder, 'customTitle', '') && _.get(currentOrder, 'customText', '')) ? (
        <ReportComponents.ExecutiveParagraphSection title={currentOrder.customTitle} body={currentOrder.customText} />
    ) : (<div />);

    // singular assessment // leftover from old way of doing assessments:
    const singularAssessment = (showAdditionalAssessments) ? <div /> : <ReportComponents.ExecutiveInfo
        title={`Recommended Special Assessment (FY ` + currentOrderChart.beginYear + `)`}
        value={<ReportComponents.Currency value={(currentOrderChart.sa)} decimals={0} />}
    />;

    // Build section
    return (
        <div>
            <ReportPageWithRegister title={pageTitle} className={className} totalPageCount={pageCount} showFooter={true} footerData={reportData}>
                <div>
                    <ReportComponents.ExecutiveParagraphSection title="" body={currentOrder.isPreliminaryStudy ? PRELIMINARY_RESERVE_STUDY_INTRO_DESCRIPTION : currentOrder.serviceLevel.introDescription} />

                    {abbreviated}

                    <div className="pdf-executive-summary-block center-block text-left">
                        <ReportComponents.ExecutiveInfo
                            title={`Projected Starting Balance as of ${reportPeriodStart}`}
                            value={<ReportComponents.Currency value={begBalance} decimals={0} />}
                        />
                        <ReportComponents.ExecutiveInfo
                            title={`Ideal Reserve Balance as of ${reportPeriodStart}`}
                            value={<ReportComponents.Currency value={idealBalance} decimals={0} />}
                        />
                        <ReportComponents.ExecutiveInfo
                            title={`Percent Funded as of ${reportPeriodStart}`}
                            value={<ReportComponents.Percentage value={percentageFunded} decimals={0} />}
                        />
                        <ReportComponents.ExecutiveInfo
                            title={`Recommended Reserve Contribution (per month)`}
                            value={<ReportComponents.Currency value={currentOrderChart.recommendedMRCVal} decimals={0} />}
                        />
                        {minimumMrc}
                        {singularAssessment}
                        {additionalAssessments}
                    </div>

                    <ReportComponents.ExecutiveParagraphSection title="Property Details" body={propertyDetails} />
                    <ReportComponents.ExecutiveParagraphSection title="Currently Programmed Projected" body={currentlyProgrammedProjects} />
                    <ReportComponents.ExecutiveParagraphSection title="Significant Reserve Projects" body={significantProjects} />
                    <ReportComponents.ExecutiveParagraphSection title="Reserve Funding" body={reserveFunding} />
                    {customExecSummary}
                </div>
            </ReportPageWithRegister>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-executive-summary.tsx